// base
import { useEffect, useRef } from 'react';

export type UseInfiniteScrollProps = {
  root?: Element | null;
  target?: React.RefObject<any>;
  onIntersect?: () => void;
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
  loading?: boolean;
};

export const useInfiniteScroll = ({
  root = null,
  target,
  onIntersect,
  threshold = 0.1,
  rootMargin = '0px',
  enabled = true,
  loading = false
}: UseInfiniteScrollProps) => {
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!enabled || loading) {
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
      return;
    }

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        onIntersect?.();
      }
    };

    observer.current = new IntersectionObserver(handleIntersect, {
      root,
      rootMargin,
      threshold
    });

    const observeTarget = target?.current;

    if (observeTarget && observeTarget instanceof Element) {
      observer.current.observe(observeTarget);
    } else {
      const checkIntersection = () => {
        const viewportHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY;

        const margin = parseInt(rootMargin, 10) || 0;

        if (scrollTop + viewportHeight >= documentHeight - margin) {
          onIntersect?.();
        }
      };

      window.addEventListener('scroll', checkIntersection);
      return () => window.removeEventListener('scroll', checkIntersection);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [target, onIntersect, threshold, root, rootMargin, enabled, loading]);

  return { loading };
};
